import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SignupBox = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 50px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 30px 20px;
    max-width: 100%;
    border: none;
    box-shadow: none;
  }
`;

const SignupHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SignupSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0 0 40px 0;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #000;
  display: block;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s;
  padding-right: ${(props) => (props.hasIcon ? "40px" : "14px")};

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }

  @media (max-width: 768px) {
    padding: 11px 12px;
    font-size: 13px;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    color: #666;
  }
`;

const PasswordStrength = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StrengthBar = styled.div`
  flex: 1;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${(props) => props.width}%;
  background: ${(props) => {
    if (props.width <= 33) return "#ff4444";
    if (props.width <= 66) return "#ffaa44";
    return "#44cc44";
  }};
  transition: all 0.3s;
`;

const StrengthText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => {
    if (props.strength === "weak") return "#ff4444";
    if (props.strength === "medium") return "#ffaa44";
    return "#44cc44";
  }};
  text-transform: uppercase;
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
`;

const SuccessMessage = styled.span`
  font-size: 12px;
  color: #44cc44;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  user-select: none;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #000;
  margin-top: 2px;
  flex-shrink: 0;
`;

const TermsText = styled.span`
  color: #666;

  a {
    color: #000;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignupButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
    margin-bottom: 18px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  color: #999;
  font-size: 13px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #eee;
  }
`;

const SocialSignup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const SocialButton = styled.button`
  padding: 12px 16px;
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #f0f0f0;
    border-color: #ddd;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const LoginSection = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const LoginText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
`;

const LoginLink = styled.a`
  font-size: 13px;
  color: #000;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    color: #666;
  }
`;

const SuccessBox = styled.div`
  background: #f0f0f0;
  border-left: 4px solid #000;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #000;
  font-weight: 500;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

// OTP Styles
const OTPSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const OTPHeading = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #000;
  margin: 0 0 8px 0;
  text-align: center;
`;

const OTPSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  text-align: center;
  margin: 0 0 20px 0;
`;

const OTPInputContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const OTPInput = styled.input`
  width: 60px;
  height: 60px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s;
  font-family: "Courier New", monospace;

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #ccc;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const ResendSection = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const ResendText = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: #000;
  text-decoration: none;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
  margin: 0;

  &:hover:not(:disabled) {
    color: #666;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const VerifyButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showOTPForm, setShowOTPForm] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [serverOTP, setServerOTP] = useState("");

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[!@#$%^&*]/.test(password)) strength += 12.5;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    if (strength <= 33) return "weak";
    if (strength <= 66) return "medium";
    return "strong";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Only allow numbers, max 10 digits
      if (!/^\d{0,10}$/.test(value)) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(formData.name)) {
      newErrors.name = "Name can only contain letters";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Terms validation
    if (!agreeTerms) {
      newErrors.terms = "You must agree to the terms and conditions";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Call OTP API
      const form = new FormData();
      form.append("phone", formData.phone);
      form.append("name", formData.name);
      form.append("password", formData.password);
      form.append("password_confirmation", formData.confirmPassword);
      const otpResp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/register/otp`,
        {
          method: "POST",
          body: form,
        },
      );
      const otpData = await otpResp.json();
      if (
        otpData.status &&
        otpData.data &&
        otpData.data.otp_preview &&
        otpData.data.otp_preview.sms_otp
      ) {
        setServerOTP(otpData.data.otp_preview.sms_otp);
        setUserPhone(formData.phone);
        setShowOTPForm(true);
        setOtpValues(["", "", "", "", "", ""]);
        setOtpError("");
      } else {
        setErrors({ submit: otpData.message || "Failed to send OTP." });
      }
    } catch (error) {
      setErrors({ submit: "Signup failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    setOtpError("");

    // Auto-focus to next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const enteredOTP = otpValues.join("");

    if (enteredOTP.length !== 6) {
      setOtpError("Please enter a 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      // Compare with server OTP
      if (enteredOTP !== serverOTP) {
        setOtpError("Invalid OTP. Please try again.");
        setOtpValues(["", "", "", "", "", ""]);
        setOtpLoading(false);
        return;
      }

      // Call registration API
      const form = new FormData();
      form.append("phone", formData.phone);
      form.append("name", formData.name);
      form.append("password", formData.password);
      form.append("password_confirmation", formData.confirmPassword);
      form.append("sms_otp", enteredOTP);
      const regResp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/register`,
        {
          method: "POST",
          body: form,
        },
      );
      const regData = await regResp.json();
      if (regData.status) {
        setSuccessMessage(
          "Account created successfully! Redirecting to login...",
        );
        setFormData({ name: "", phone: "", password: "", confirmPassword: "" });
        setPasswordStrength(0);
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        setOtpError(
          regData.message || "Registration failed. Please try again.",
        );
      }
    } catch (error) {
      setOtpError("OTP verification or registration failed. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSocialSignup = (provider) => {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container>
      {/* <Navbar /> */}

      <ContentWrapper>
        <SignupBox>
          <SignupHeading>Create Account</SignupHeading>
          <SignupSubtitle>
            Join us and start shopping with exclusive benefits
          </SignupSubtitle>

          {successMessage && <SuccessBox>{successMessage}</SuccessBox>}

          {showOTPForm ? (
            // OTP Verification Form
            <OTPSection>
              <OTPHeading>Verify Your Phone</OTPHeading>
              <OTPSubtitle>
                We've sent a 6-digit OTP to <strong>{userPhone}</strong>. Enter
                it below to verify your account.
              </OTPSubtitle>

              <form onSubmit={handleVerifyOTP}>
                <OTPInputContainer>
                  {otpValues.map((value, index) => (
                    <OTPInput
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={value}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      disabled={otpLoading}
                      placeholder="•"
                    />
                  ))}
                </OTPInputContainer>

                {otpError && <ErrorMessage>{otpError}</ErrorMessage>}

                <VerifyButton type="submit" disabled={otpLoading}>
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </VerifyButton>
              </form>

              <ResendSection>
                <ResendText>Didn't receive the OTP?</ResendText>
                <ResendButton
                  type="button"
                  disabled={otpLoading}
                  onClick={() => {
                    setOtpValues(["", "", "", "", "", ""]);
                    setOtpError("");
                  }}
                >
                  Resend OTP
                </ResendButton>
              </ResendSection>
            </OTPSection>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Name Field */}
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  disabled={loading}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              {/* Phone Field */}
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="10-digit phone number"
                  maxLength="10"
                  disabled={loading}
                />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </FormGroup>

              {/* Password Field */}
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <InputWrapper>
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a strong password"
                    hasIcon={true}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    disabled={loading}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </PasswordToggle>
                </InputWrapper>

                {/* Password Strength Indicator */}
                {formData.password && (
                  <PasswordStrength>
                    <StrengthBar>
                      <StrengthFill width={passwordStrength} />
                    </StrengthBar>
                    <StrengthText
                      strength={getPasswordStrengthText(passwordStrength)}
                    >
                      {getPasswordStrengthText(passwordStrength)}
                    </StrengthText>
                  </PasswordStrength>
                )}

                {errors.password && (
                  <ErrorMessage>{errors.password}</ErrorMessage>
                )}
              </FormGroup>

              {/* Confirm Password Field */}
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <InputWrapper>
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    hasIcon={true}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </PasswordToggle>
                </InputWrapper>

                {formData.password &&
                  formData.confirmPassword &&
                  formData.password === formData.confirmPassword && (
                    <SuccessMessage>
                      <CheckOutlined />
                      Passwords match
                    </SuccessMessage>
                  )}

                {errors.confirmPassword && (
                  <ErrorMessage>{errors.confirmPassword}</ErrorMessage>
                )}
              </FormGroup>

              {/* Terms & Conditions */}
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => {
                    setAgreeTerms(e.target.checked);
                    if (errors.terms) {
                      setErrors((prev) => ({ ...prev, terms: "" }));
                    }
                  }}
                  disabled={loading}
                />
                <TermsText>
                  I agree to the{" "}
                  <a href="/terms-and-conditions" target="_blank">
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/privacy-policy" target="_blank">
                    Privacy Policy
                  </a>
                </TermsText>
              </CheckboxLabel>

              {errors.terms && <ErrorMessage>{errors.terms}</ErrorMessage>}

              {/* Signup Button */}
              <SignupButton type="submit" disabled={loading || !agreeTerms}>
                {loading ? "Creating account..." : "Create Account"}
              </SignupButton>
            </form>
          )}

          {/* Login Link */}
          <LoginSection>
            <LoginText>Already have an account?</LoginText>
            <LoginLink
              onClick={() => {
                navigate("/login");
              }}
            >
              Sign in here
            </LoginLink>
          </LoginSection>
        </SignupBox>
      </ContentWrapper>

      <Footer />
    </Container>
  );
};

export default Signup;
